/**
 * Russian plurals.
 *
 * The shop counts things out loud — «1 товар», «2 товара», «5 товаров» — and
 * Russian picks between three forms by the last one or two digits, not by
 * "one versus everything else". Getting it wrong is not a subtle bug: «5 товар»
 * is the single clearest signal to a Russian customer that nobody who speaks
 * the language has read the screen, and it lands on the most visible number the
 * storefront publishes.
 *
 * Written out rather than delegated to `Intl.PluralRules`. That API knows this
 * rule and would be the right answer if the rule were in doubt — but it needs
 * the CLDR data for `ru` to be present, and a runtime built without it does not
 * fail, it silently answers with the English rule and declines every noun in
 * this file wrongly. Six lines that cannot be un-shipped by a Docker base image
 * are worth more here than a call that is correct only conditionally, and the
 * rule below is pinned by `plural.test.ts` on exactly the numbers that separate
 * it from the naive version.
 */

/** The three forms, in the order the rule chooses between them: 1 / 2 / 5. */
export type PluralForms = readonly [one: string, few: string, many: string];

export type PluralForm = 'one' | 'few' | 'many';

/** «товар / товара / товаров» — the noun the storefront counts in. */
export const ITEMS: PluralForms = ['товар', 'товара', 'товаров'];

/**
 * What is actually being counted.
 *
 * These are counts of whole things — orders delivered, products behind a
 * service — so a negative, a fraction or a `NaN` arriving here is a caller bug
 * rather than a case with a correct Russian rendering. Each is folded to the
 * nearest number this module can decline truthfully instead of being allowed
 * through to print «NaN товаров» on the owner's home screen. `pluralize` counts
 * with the same folded value, so the numeral and its noun can never disagree.
 */
const wholeCount = (count: number): number =>
  Number.isFinite(count) ? Math.trunc(Math.abs(count)) : 0;

/**
 * Which of the three forms a number takes.
 *
 * The exception is the whole point: 11–14 take the «много» form regardless of
 * their last digit, so 11 is «товаров» while 21 is «товар», and it repeats every
 * hundred — 111 and 112 are «товаров» too. A rule written on the last digit
 * alone passes for 1, 2 and 5 and fails on every one of those.
 */
export function pluralForm(count: number): PluralForm {
  const whole = wholeCount(count);
  const lastTwo = whole % 100;
  const last = whole % 10;

  if (lastTwo >= 11 && lastTwo <= 14) return 'many';
  if (last === 1) return 'one';
  if (last >= 2 && last <= 4) return 'few';
  return 'many';
}

/** The noun alone, for a caller that sets the numeral itself. */
export function plural(count: number, forms: PluralForms): string {
  const form = pluralForm(count);
  return form === 'one' ? forms[0] : form === 'few' ? forms[1] : forms[2];
}

/**
 * «1 240 товаров» — the numeral and its noun, as one unbreakable token.
 *
 * Grouped through the Russian locale, which separates thousands with a
 * non-breaking space, and joined to the noun with another one: a counter that
 * wraps between the number and the thing it counted reads as two facts instead
 * of one, and on a 390px screen it will.
 */
export function pluralize(count: number, forms: PluralForms): string {
  const whole = wholeCount(count);
  // `\u00A0` written as an escape rather than typed: a literal non-breaking
  // space is indistinguishable from an ordinary one in every editor and every
  // diff, and this character is the entire point of the line.
  return `${new Intl.NumberFormat('ru-RU').format(whole)}\u00A0${plural(whole, forms)}`;
}
